// frontend/src/components/EmpresaPortal/StacksSugeridos.js
//
// Stacks sugeridos por PsyTest (los arma el admin). La empresa los puede
// usar libremente al invitar candidatos y decide su propia regla de
// reaplicación para cada uno; la del admin es solo la sugerencia por
// defecto. Backend: GET/PUT /api/empresas/me/stacks-sugeridos.

import React, { useState, useEffect } from 'react';
import { Sparkles, Check } from 'lucide-react';

const textoDias = (dias) => (dias ? `Cada ${dias} día${dias !== 1 ? 's' : ''}` : 'Sin límite');

// Estado del selector a partir de lo guardado
const reglaInicial = (s) => {
  if (!s.personalizado) return { modo: 'sugerida', dias: s.dias_sugeridos || 30 };
  if (s.dias_empresa === null) return { modo: 'sin_limite', dias: s.dias_sugeridos || 30 };
  return { modo: 'dias', dias: s.dias_empresa };
};

const StacksSugeridos = () => {
  const [stacks, setStacks] = useState([]);
  const [reglas, setReglas] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(null);
  const [guardado, setGuardado] = useState(null);
  const [error, setError] = useState('');

  const cargar = async () => {
    try {
      const response = await fetch('/api/empresas/me/stacks-sugeridos', { credentials: 'include' });
      const data = await response.json().catch(() => []);
      if (!response.ok) throw new Error(data.error || 'No se pudieron cargar los stacks sugeridos');
      setStacks(data);
      setReglas(Object.fromEntries(data.map(s => [s.id, reglaInicial(s)])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async (stack, regla) => {
    setGuardando(stack.id);
    setError('');
    try {
      const response = await fetch(`/api/empresas/me/stacks-sugeridos/${stack.id}/reaplicacion`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modo: regla.modo, dias: regla.dias })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo guardar');
      setGuardado(stack.id);
      setTimeout(() => setGuardado(g => (g === stack.id ? null : g)), 2000);
    } catch (err) {
      setError(err.message);
      setReglas(r => ({ ...r, [stack.id]: reglaInicial(stack) }));
    } finally {
      setGuardando(null);
    }
  };

  const cambiarModo = (stack, modo) => {
    const regla = { ...reglas[stack.id], modo };
    setReglas(r => ({ ...r, [stack.id]: regla }));
    // "Esperar N días" se guarda con su botón, para poder escribir el número
    if (modo !== 'dias') guardar(stack, regla);
  };

  const cambiarDias = (stack, valor) => {
    setReglas(r => ({ ...r, [stack.id]: { ...r[stack.id], dias: valor } }));
  };

  return (
    <div className="ep-card ep-stacks-sugeridos">
      <div className="ep-card-header">
        <h2><Sparkles size={18} /> Stacks sugeridos por PsyTest</h2>
      </div>
      <p className="ep-hint">
        Úsalos libremente al invitar candidatos. Te sugerimos cada cuánto se puede repetir cada stack con el mismo
        candidato, pero tú decides la regla para tu empresa.
      </p>

      {error && <p className="ep-sugeridos-error">{error}</p>}

      {loading ? (
        <p className="ep-empty">Cargando...</p>
      ) : stacks.length === 0 ? (
        <p className="ep-empty">Por ahora no hay stacks sugeridos.</p>
      ) : (
        <div className="ep-table-wrap">
          <table className="ep-table">
            <thead>
              <tr>
                <th>Stack</th>
                <th>Reaplicación sugerida</th>
                <th>Reaplicación para tu empresa</th>
              </tr>
            </thead>
            <tbody>
              {stacks.map(s => {
                const regla = reglas[s.id] || reglaInicial(s);
                const ocupado = guardando === s.id;
                return (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.nombre}</strong>
                      <div className="ep-sugerido-pruebas">
                        {s.pruebas.map(p => p.nombre).join(' · ') || 'Sin pruebas'}
                      </div>
                    </td>
                    <td>{textoDias(s.dias_sugeridos)}</td>
                    <td>
                      <div className="ep-sugerido-regla">
                        <select
                          value={regla.modo}
                          onChange={(e) => cambiarModo(s, e.target.value)}
                          disabled={ocupado}
                          aria-label={`Reaplicación de ${s.nombre} para tu empresa`}
                        >
                          <option value="sugerida">Usar la sugerida ({textoDias(s.dias_sugeridos).toLowerCase()})</option>
                          <option value="sin_limite">Sin límite</option>
                          <option value="dias">Esperar un número de días</option>
                        </select>
                        {regla.modo === 'dias' && (
                          <>
                            <input
                              type="number"
                              min="1"
                              max="3650"
                              value={regla.dias}
                              onChange={(e) => cambiarDias(s, e.target.value)}
                              disabled={ocupado}
                              aria-label="Días de espera"
                            />
                            <span>días</span>
                            <button
                              type="button"
                              className="ep-btn-primary ep-btn-sm"
                              onClick={() => guardar(s, regla)}
                              disabled={ocupado || !(parseInt(regla.dias, 10) >= 1)}
                            >
                              Guardar
                            </button>
                          </>
                        )}
                        {guardado === s.id && <span className="ep-sugerido-ok"><Check size={14} /> Guardado</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StacksSugeridos;
